import { Dialog, Transition } from '@headlessui/react';
import {
  BookOpenIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment } from 'react';

const toneStyles = {
  info: {
    container: 'border-sky-100 bg-sky-50/70',
    icon: 'bg-sky-100 text-sky-700',
  },
  success: {
    container: 'border-emerald-100 bg-emerald-50/70',
    icon: 'bg-emerald-100 text-emerald-700',
  },
  warning: {
    container: 'border-amber-100 bg-amber-50/80',
    icon: 'bg-amber-100 text-amber-700',
  },
  security: {
    container: 'border-indigo-100 bg-indigo-50/70',
    icon: 'bg-indigo-100 text-indigo-700',
  },
};

const toneIcon = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  security: ShieldCheckIcon,
};

function HelpBlock({ block }) {
  const tone = block.tone || 'info';
  const styles = toneStyles[tone] || toneStyles.info;
  const Icon = toneIcon[tone] || InformationCircleIcon;

  return (
    <article className={clsx('rounded-2xl border p-4', styles.container)}>
      <div className="flex items-start gap-3">
        <span className={clsx('mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', styles.icon)}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-950">{block.title}</h3>
          {block.description ? <p className="mt-1 text-sm leading-6 text-slate-600">{block.description}</p> : null}
          {Array.isArray(block.items) && block.items.length ? (
            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
              {block.items.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function ContextHelpDrawer({
  open,
  onClose,
  title,
  description,
  sections,
  activeSectionKey,
  onSectionChange,
}) {
  const safeSections = Array.isArray(sections) ? sections : [];
  const activeSection = safeSections.find((section) => section.key === activeSectionKey) || safeSections[0];

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/35 backdrop-blur-[1px]" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto flex h-full w-screen flex-col bg-white shadow-2xl sm:max-w-xl">
                  <div className="border-b border-slate-200 bg-slate-50 px-5 py-5 sm:px-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">Ajuda contextual</p>
                        <Dialog.Title className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
                          {title}
                        </Dialog.Title>
                        {description ? <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p> : null}
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        aria-label="Fechar ajuda contextual"
                      >
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>

                    {safeSections.length > 1 ? (
                      <>
                        <div className="mt-4 sm:hidden">
                          <label htmlFor="waitercall-context-help-section" className="sr-only">
                            Selecionar tópico da ajuda
                          </label>
                          <select
                            id="waitercall-context-help-section"
                            value={activeSection?.key || ''}
                            onChange={(event) => onSectionChange?.(event.target.value)}
                            className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          >
                            {safeSections.map((section) => (
                              <option key={section.key} value={section.key}>
                                {section.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mt-5 hidden flex-wrap gap-2 sm:flex">
                          {safeSections.map((section) => (
                            <button
                              key={section.key}
                              type="button"
                              onClick={() => onSectionChange?.(section.key)}
                              className={clsx(
                                'rounded-full border px-3 py-2 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                                activeSection?.key === section.key
                                  ? 'border-indigo-200 bg-indigo-600 text-white shadow-sm'
                                  : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700'
                              )}
                            >
                              {section.label}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                    {activeSection ? (
                      <div className="space-y-5">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
                              <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <div>
                              <h2 className="text-base font-semibold text-slate-950">{activeSection.title}</h2>
                              {activeSection.description ? (
                                <p className="mt-1 text-sm leading-6 text-slate-600">{activeSection.description}</p>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {Array.isArray(activeSection.highlights) && activeSection.highlights.length ? (
                          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Pontos de atenção</p>
                            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
                              {activeSection.highlights.map((item) => (
                                <li key={item} className="flex gap-2">
                                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        <div className="space-y-3">
                          {(activeSection.blocks || []).map((block) => (
                            <HelpBlock key={block.title} block={block} />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                        <p className="text-sm font-semibold text-slate-900">Nenhum conteúdo de ajuda disponível</p>
                        <p className="mt-1 text-sm text-slate-500">Esta tela ainda não possui orientações configuradas.</p>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
